import React, { useId } from 'react'
import styled from '@emotion/styled'
import { MFAMethod } from '@mobi/api-types'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon, type IconName } from '@mobi/component-library/Assets/Icon/Icon'
import { lessThan } from '@mobi/component-library/Utils/media'
import { getMFAMethodLabel } from '../Helpers/helpers'

export const MFASelectionItem: React.FC<{
  iconName: IconName
  mfaMethod: MFAMethod
  isVerified: boolean
  id: string
  isToggled: boolean
  contactInfo: string | undefined
  onToggle: () => void
  onVerify: () => void
  onEditContact: () => void
  isLoading?: boolean
}> = ({
  iconName,
  mfaMethod,
  isVerified,
  id,
  isToggled,
  contactInfo,
  onToggle,
  onVerify,
  onEditContact,
  isLoading = false,
}) => {
  const itemId = useId()
  const hasContact = !!contactInfo

  return (
    <SelectionWrapperStyled
      data-testid={`mfa-${mfaMethod === 'SMS' ? 'phone' : 'email'}-verification`}
    >
      <IconWrapperStyled>
        <Icon name={iconName} />
      </IconWrapperStyled>
      <ContentStyled>
        <ContentLeftStyled>
          <BadgeStyled className='badge' isVerified={isVerified}>
            <Icon
              name='SolidCheckCircle'
              size='12px'
              color={isVerified ? colors.sushi[500] : colors.error[600]}
            />
            <p className='badge-status'>{isVerified ? 'Verified' : 'Not verified'}</p>
          </BadgeStyled>
          <p className='method-label'>{getMFAMethodLabel(mfaMethod)}</p>
          {isLoading ? (
            <p className='contact-value no-contact'>Loading...</p>
          ) : (
            <p className='contact-value no-contact'>{contactInfo || 'No contact provided'}</p>
          )}
        </ContentLeftStyled>
        {isVerified ? (
          <Toggle
            id={`${id}-${itemId}`}
            isOn={isToggled}
            onToggle={onToggle}
            disabled={!hasContact}
          />
        ) : hasContact ? (
          <SecondaryButton onClick={onVerify}>Verify Now</SecondaryButton>
        ) : (
          <SecondaryButton onClick={onEditContact}>Add</SecondaryButton>
        )}
      </ContentStyled>
    </SelectionWrapperStyled>
  )
}

const SecondaryButton = ({ children, ...props }: React.ComponentProps<typeof Button>) => {
  return (
    <Button size='xxs' color='secondary_grey' style={{ flexShrink: 0 }} {...props}>
      {children}
    </Button>
  )
}

// ======
// Styles
// ======

const SelectionWrapperStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const ContentStyled = styled.div({
  display: 'flex',
  flexGrow: 2,
  padding: `${spacing.md} ${spacing.md} ${spacing.md}`,
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `0.5px solid ${colors.neutral[200]}`,
  gap: spacing.smx2,

  [lessThan('px375')]: {
    padding: `${spacing.sm} ${spacing.sm} ${spacing.sm}`,
  },
})

const ContentLeftStyled = styled.div({
  display: 'flex',
  flexGrow: 2,
  flexShrink: 1,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  fontFamily: font.family.primary,

  p: {
    margin: 0,
    minWidth: 0,
  },

  '.method-label': {
    fontWeight: font.weight.semibold,
  },

  '.contact-value': {
    ...font.size.md,
    fontWeight: font.weight.regular,
    color: colors.neutral[800],
    wordBreak: 'break-word',
  },
})

const BadgeStyled = styled.div<{ isVerified?: boolean }>(({ isVerified }) => ({
  display: 'flex',
  padding: `${spacing.none} ${spacing.smx1}`,
  alignItems: 'center',
  background: isVerified ? colors.sushi[50] : colors.error[50],
  borderRadius: radius.md,
  gap: spacing.smx2,

  '.badge-status': {
    color: isVerified ? colors.sushi[600] : colors.error[600],
    ...font.size.md,
    fontWeight: font.weight.medium,
  },
}))

const IconWrapperStyled = styled.div({
  display: 'flex',
  padding: '10px',
  backgroundColor: colors.neutral[100],
  borderRadius: radius.full,
  svg: {
    width: '16px',
    height: '16px',
  },

  [lessThan('px375')]: {
    padding: '0',
  },
})
